import type { PeriodOverPeriodChart } from "app/pages/industry-analysis/segmentation/size-and-growth-overview/common/types";
import type { FC } from "react";
import DurationService from "app/services/DurationService";
import { useCurrentStateAndParams } from "@uirouter/react";
import { COLORS } from "app/pages/industry-analysis/segmentation/size-and-growth-overview/common/const";
import styled from "styled-components";
import { minVisitsAbbrFilter, percentageSignFilter } from "app/filters/ngFilters";
import { ChangeValue } from "@similarweb/ui-components";
import { LegendItem } from "app/pages/industry-analysis/common/LegendItem";
import { Subtitle } from "pro-features/components/BoxSubtitle/src/styled";
import { translate } from "app/filters/i18nFilter";
import { Subtitle as SubtitleView, Bold } from "pro-features/components/BoxSubtitle/src/styled";
import { InfoTooltip } from "app/components/React/Tooltip/InfoTooltip/InfoTooltip";

const { BLUE, ORANGE } = COLORS;

type ILegends = PeriodOverPeriodChart;

interface ISubtitle {
    subtitleText: string;
    boldSubtitlePrefix: string;
    infoTooltipText: string;
    subtitlePrefix: string;
}

const Container = styled.div`
    display: flex;
    gap: 16px;
    padding: 12px 24px 0px 24px;
`;

const ChangeContainer = styled.div`
    display: flex;
    gap: 8px;
`;

const SubTitleContainer = styled.div`
    padding-left: 12px;
    display: flex;
    gap: 4px;
`;

const InfoIconContainer = styled.div`
    margin-top: -1px;
`;

export const Legends: FC<ILegends> = (props) => {
    const { chartState } = props;
    const { rawData } = chartState;
    const { params } = useCurrentStateAndParams();
    if (!rawData) {
        return null;
    }
    const { Totals: totals } = rawData;
    const { PrevAvgVisits: previousAverageVisits, VisitsChange: change, AvgVisits: averageVisits } = totals;
    const { duration, comparedDuration } = params;
    const isPreviousYear = comparedDuration === "12m";
    const { forWidget } = DurationService.getDurationData(duration, comparedDuration);
    const formatter = minVisitsAbbrFilter();
    if (!Array.isArray(forWidget)) {
        return null;
    }
    const [currentPeriod, previousPeriod] = forWidget;
    const legends = [
        { date: previousPeriod, value: previousAverageVisits, labelColor: ORANGE },
        {
            date: currentPeriod,
            value: averageVisits,
            labelColor: BLUE,
        },
    ];

    const subtitle = (props: ISubtitle) => {
        const { subtitleText, boldSubtitlePrefix, subtitlePrefix, infoTooltipText } = props;
        return (
            <SubTitleContainer>
                <span>{subtitlePrefix}</span>
                <InfoTooltip
                    infoText={infoTooltipText}
                    infoIconContainer={InfoIconContainer}
                />
                <Bold>{boldSubtitlePrefix}</Bold>
                <SubtitleView>{subtitleText}</SubtitleView>
            </SubTitleContainer>
        );
    };

    return (
        <Container>
            {legends.map((legend, index) => {
                const { date, labelColor, value } = legend;
                return (
                    <LegendItem
                        color={labelColor}
                        title={date}
                        subtitle={subtitle({
                            subtitleText: translate("wa.ao.graph.avgvisits"),
                            boldSubtitlePrefix: formatter(value),
                            subtitlePrefix: translate("market.segmentation.size.growth.bar.chart.subtitle"),
                            infoTooltipText: translate("market.segmentation.size.growth.bar.chart.subtitle.tooltip"),
                        })}
                        key={index}
                    />
                );
            })}
            <ChangeContainer>
                <ChangeValue
                    value={percentageSignFilter()(change, 2)}
                    isDecrease={change < 0}
                />
                <Subtitle>{translate(isPreviousYear ? "si.common.period.yoy" : "common.pop")}</Subtitle>
            </ChangeContainer>
        </Container>
    );
};
